import { SiteHeader } from "@/components/site-header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { useMapWs, type NeighborUser } from "@/hooks/use-map-ws";
import { useIsMobile } from "@/hooks/use-mobile";
import { withPMTilesSource } from "@/lib/map-style";
import { cachedPMTiles } from "@/lib/pmtiles-cache";
import { cn } from "@/lib/utils";
import { queryKeys } from "@/lib/queryKeys";
import { adminProvider, type LoginHeatmapCell } from "@/providers/admin.provider";
import { locationsProvider, type MapObj } from "@/providers/locations.provider";
import { usersProvider, type LocationUpdate } from "@/providers/users.provider";
import { photoSpotsProvider, type PhotoSpotListItem } from "@/providers/photo-spots.provider";
import { shardingProvider, type ServerNode } from "@/providers/sharding.provider";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { cellToBoundary, latLngToCell, polygonToCells } from "h3-js";
import * as LucideIcons from "lucide-react";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { IconAdjustmentsHorizontal, IconX } from "@tabler/icons-react";
import { Protocol } from "pmtiles";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

const PMTILES_URL = process.env.BUN_PUBLIC_PMTILES_URL!;
const MAP_STYLE_URL = process.env.BUN_PUBLIC_MAP_STYLE_URL!;
const USER_TTL_MS = 10_000;
const SMOOTH_MS = 333;
const H3_MAX_CELLS = 4_000;
const LINK_SOURCE_ID = "user-server-links";
const LINK_LAYER_ID = "user-server-links-line";

// ── Polyline decoder ─────────────────────────────────────────────────────────
function decodePolyline(encoded: string): [number, number][] {
  const coords: [number, number][] = [];
  let index = 0, lat = 0, lng = 0;
  while (index < encoded.length) {
    let b: number, shift = 0, result = 0;
    do { b = encoded.charCodeAt(index++) - 63; result |= (b & 0x1f) << shift; shift += 5; } while (b >= 0x20);
    lat += result & 1 ? ~(result >> 1) : result >> 1;
    shift = 0; result = 0;
    do { b = encoded.charCodeAt(index++) - 63; result |= (b & 0x1f) << shift; shift += 5; } while (b >= 0x20);
    lng += result & 1 ? ~(result >> 1) : result >> 1;
    coords.push([lng / 1e5, lat / 1e5]);
  }
  return coords;
}

// ── Helpers ───────────────────────────────────────────────────────────────────
function haversineMeters(
  a: { lat: number; lng: number },
  b: { lat: number; lng: number },
) {
  const R = 6_371_000;
  const toRad = (x: number) => (x * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

function h3CellsForBounds(bounds: maplibregl.LngLatBounds, res: number): string[] {
  const w = bounds.getWest();
  const s = bounds.getSouth();
  const e = bounds.getEast();
  const n = bounds.getNorth();
  const ring: [number, number][] = [
    [w, s],
    [e, s],
    [e, n],
    [w, n],
    [w, s],
  ];
  return polygonToCells(ring, res, true);
}

function h3CellsToGeoJson(cells: string[]) {
  return {
    type: "FeatureCollection" as const,
    features: cells.map((cell) => {
      const boundary = cellToBoundary(cell, true) as [number, number][];
      return {
        type: "Feature" as const,
        properties: {},
        geometry: {
          type: "Polygon" as const,
          coordinates: [[...boundary, boundary[0]]],
        },
      };
    }),
  };
}

function loginHeatmapToGeoJson(cells: LoginHeatmapCell[]) {
  return {
    type: "FeatureCollection" as const,
    features: cells.map((cell) => {
      const boundary = cellToBoundary(cell.h3_index, true) as [number, number][];
      return {
        type: "Feature" as const,
        properties: { h3: cell.h3_index, count: cell.count },
        geometry: {
          type: "Polygon" as const,
          coordinates: [[...boundary, boundary[0]]],
        },
      };
    }),
  };
}

// Backend only stores/accepts H3 res3–res7 for the login heatmap. Roughly:
// res3 ~12,000km² (country/region) down to res7 ~5km² (city district), ~7x per step.
function heatmapResForZoom(zoom: number): 3 | 4 | 5 | 6 | 7 {
  if (zoom <= 4) return 3;
  if (zoom <= 5) return 4;
  if (zoom <= 7) return 5;
  if (zoom <= 9) return 6;
  return 7;
}

function createArrowEl(_clubed: boolean, heading: number, color = "#ffffff") {
  const el = document.createElement("div");
  el.style.cssText =
    "width:16px;height:16px;cursor:pointer;display:flex;align-items:center;justify-content:center;filter:drop-shadow(0 0 2px rgba(0,0,0,0.8))";
  el.innerHTML = `<svg viewBox="0 0 24 24" width="16" height="16" fill="${color}" stroke="white" stroke-width="1.5" style="transform:rotate(${heading}deg);transition:none"><polygon points="12,2 22,20 12,16 2,20"/></svg>`;
  return el;
}

function createLabelEl(username: string, clubName?: string | null) {
  const el = document.createElement("div");
  el.style.cssText =
    "background:transparent;border:none;box-shadow:none;white-space:nowrap;pointer-events:none";
  el.innerHTML = `
    <div style="display:flex;flex-direction:column;align-items:center;gap:2px;margin-top:4px">
      <div style="background:rgba(15,23,42,0.85);color:#e2e8f0;font-size:11px;font-family:monospace;font-weight:600;padding:1px 5px;border-radius:3px;line-height:1.4">${username}</div>
      ${clubName ? `<div style="background:rgba(21,128,61,0.85);color:#bbf7d0;font-size:10px;font-family:monospace;padding:1px 5px;border-radius:3px;line-height:1.4">${clubName}</div>` : ""}
    </div>`;
  return el;
}

const CURRENT_USER_COLOR = "#fbbf24";

// The snapshot layer adds the node holding the user's websocket — the one piece
// of data the live stream's label doesn't carry.
function createCurrentUserLabelEl(
  username: string,
  clubName?: string,
  serverId?: string,
) {
  const el = document.createElement("div");
  el.style.cssText =
    "background:transparent;border:none;box-shadow:none;white-space:nowrap;pointer-events:none";
  el.innerHTML = `
    <div style="display:flex;flex-direction:column;align-items:center;gap:2px;margin-top:4px">
      <div style="background:rgba(15,23,42,0.85);color:${CURRENT_USER_COLOR};font-size:11px;font-family:monospace;font-weight:600;padding:1px 5px;border-radius:3px;line-height:1.4">${username}</div>
      ${clubName ? `<div style="background:rgba(21,128,61,0.85);color:#bbf7d0;font-size:10px;font-family:monospace;padding:1px 5px;border-radius:3px;line-height:1.4">${clubName}</div>` : ""}
      ${serverId ? `<div style="background:rgba(15,23,42,0.85);color:#94a3b8;font-size:9px;font-family:monospace;padding:0 4px;border-radius:3px;line-height:1.4">${serverId}</div>` : ""}
    </div>`;
  return el;
}

type AnimatedMarker = maplibregl.Marker & { __animId?: number };

function animateMarker(
  marker: AnimatedMarker,
  to: { lat: number; lng: number },
  duration: number,
) {
  if (marker.__animId) cancelAnimationFrame(marker.__animId);
  const from = marker.getLngLat();
  const start = Date.now();
  const tick = () => {
    const t = Math.min((Date.now() - start) / duration, 1);
    const ease = 1 - (1 - t) ** 3;
    marker.setLngLat([
      from.lng + (to.lng - from.lng) * ease,
      from.lat + (to.lat - from.lat) * ease,
    ]);
    if (t < 1) {
      marker.__animId = requestAnimationFrame(tick);
    } else {
      marker.setLngLat([to.lng, to.lat]);
    }
  };
  marker.__animId = requestAnimationFrame(tick);
}

// ── Types ─────────────────────────────────────────────────────────────────────
interface MarkerEntry {
  dot: AnimatedMarker;
  label: AnimatedMarker;
}

interface UserEntry extends NeighborUser {
  _ttl: number;
}

// ── Route pin helpers ─────────────────────────────────────────────────────────
function createRoutePinEl(kind: "origin" | "dest") {
  const color = kind === "origin" ? "rgba(34,197,94,0.95)" : "rgba(239,68,68,0.95)";
  const label = kind === "origin" ? "A" : "B";
  const el = document.createElement("div");
  el.style.cssText = "display:flex;flex-direction:column;align-items:center;filter:drop-shadow(0 1px 3px rgba(0,0,0,0.6))";
  el.innerHTML = `
    <div style="background:${color};color:#fff;font-size:11px;font-family:monospace;font-weight:700;width:20px;height:20px;border-radius:50%;display:flex;align-items:center;justify-content:center">${label}</div>
    <div style="width:0;height:0;border-left:5px solid transparent;border-right:5px solid transparent;border-top:6px solid ${color}"></div>`;
  return el;
}

// ── Search pin helpers ────────────────────────────────────────────────────────
function createSearchPinEl(name: string, onRemove: () => void) {
  const el = document.createElement("div");
  el.style.cssText = "position:relative;display:flex;flex-direction:column;align-items:center;cursor:default;filter:drop-shadow(0 1px 3px rgba(0,0,0,0.6))";

  const label = document.createElement("div");
  label.style.cssText = "background:rgba(239,68,68,0.95);color:#fff;font-size:11px;font-family:monospace;font-weight:600;padding:2px 22px 2px 7px;border-radius:4px;white-space:nowrap;max-width:180px;overflow:hidden;text-overflow:ellipsis;position:relative";
  label.textContent = name;

  const btn = document.createElement("button");
  btn.textContent = "×";
  btn.style.cssText = "position:absolute;right:4px;top:50%;transform:translateY(-50%);background:none;border:none;color:#fff;cursor:pointer;font-size:13px;line-height:1;padding:0;opacity:0.8";
  const stop = (e: Event) => e.stopPropagation();
  btn.addEventListener("pointerdown", stop);
  btn.addEventListener("mousedown", stop);
  btn.addEventListener("click", (e) => { e.stopPropagation(); onRemove(); });

  const caret = document.createElement("div");
  caret.style.cssText = "width:0;height:0;border-left:5px solid transparent;border-right:5px solid transparent;border-top:6px solid rgba(239,68,68,0.95)";

  label.appendChild(btn);
  el.appendChild(label);
  el.appendChild(caret);
  return el;
}

// ── Map object marker helpers ─────────────────────────────────────────────────
function kebabToPascal(str: string): string {
  return str.split("-").map((s) => s.charAt(0).toUpperCase() + s.slice(1)).join("");
}

function lucideIconToSvg(iconName: string, color: string, size: number): string {
  const key = kebabToPascal(iconName) as keyof typeof LucideIcons;
  const Icon = LucideIcons[key] as any;
  // Call the forwardRef render fn to extract the closed-over iconNode without React rendering
  const node: [string, Record<string, string>][] | undefined =
    Icon?.render?.({ size, color }, null)?.props?.iconNode;

  const fallback = `<circle cx="12" cy="12" r="9"/>`;
  const children = node
    ? node.map(([tag, { key: _k, ...attrs }]) => {
        const attrStr = Object.entries(attrs).map(([k, v]) => `${k}="${v}"`).join(" ");
        return `<${tag} ${attrStr}/>`;
      }).join("")
    : fallback;

  return `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${children}</svg>`;
}

let mapObjBalloonSeq = 0;

function createImageBalloonSvg(imageUrl: string, primaryColor: string, secondaryColor: string): string {
  const clipId = `map-obj-balloon-clip-${mapObjBalloonSeq++}`;
  return `<svg width="50" height="60" viewBox="0 0 34 41" xmlns="http://www.w3.org/2000/svg">
    <defs><clipPath id="${clipId}"><circle cx="17" cy="16" r="12" /></clipPath></defs>
    <path d="M17 2C9.3 2 3 8.1 3 15.6c0 10.9 14 22.4 14 22.4s14-11.5 14-22.4C31 8.1 24.7 2 17 2z" fill="${primaryColor}" stroke="${primaryColor}" stroke-width="2.5"/>
    <image href="${imageUrl}" x="5" y="4" width="24" height="24" clip-path="url(#${clipId})" preserveAspectRatio="xMidYMid slice"/>
    <circle cx="17" cy="16" r="12" fill="none" stroke="${secondaryColor}" stroke-width="1.5"/>
  </svg>`;
}

function createMapObjEl(obj: MapObj): HTMLElement {
  const el = document.createElement("div");
  el.style.cssText =
    "display:flex;flex-direction:column;align-items:center;filter:drop-shadow(0 1px 4px rgba(0,0,0,0.6));cursor:pointer";

  if (obj.image_url) {
    el.style.position = "relative";
    el.innerHTML = `
      ${createImageBalloonSvg(obj.image_url, obj.primary_color, obj.secondary_color)}
      <div style="position:absolute;top:100%;left:50%;transform:translateX(-50%);margin-top:3px;background:rgba(15,23,42,0.85);color:#e2e8f0;font-size:10px;font-family:monospace;font-weight:600;padding:1px 5px;border-radius:3px;line-height:1.4;white-space:nowrap">${obj.name}</div>`;
  } else {
    const iconSvg = lucideIconToSvg(obj.icon_name ?? "circle-slash-2", obj.primary_color, 16);
    el.innerHTML = `
      <div style="background:${obj.secondary_color};border:2px solid ${obj.primary_color};border-radius:8px;padding:5px;display:flex;align-items:center;justify-content:center">${iconSvg}</div>
      <div style="background:rgba(15,23,42,0.85);color:#e2e8f0;font-size:10px;font-family:monospace;font-weight:600;padding:1px 5px;border-radius:3px;line-height:1.4;margin-top:3px;white-space:nowrap">${obj.name}</div>
      <div style="width:0;height:0;border-left:4px solid transparent;border-right:4px solid transparent;border-top:5px solid ${obj.secondary_color}"></div>`;
  }
  return el;
}

// ── Servers ───────────────────────────────────────────────────────────────────
function createServerEl(server: ServerNode): HTMLElement {
  const el = document.createElement("div");
  el.style.cssText =
    "display:flex;flex-direction:column;align-items:center;filter:drop-shadow(0 1px 4px rgba(0,0,0,0.7))";
  el.innerHTML = `
    <div style="background:rgba(15,23,42,0.9);border:2px solid rgba(56,189,248,0.9);border-radius:8px;padding:5px;display:flex;align-items:center;justify-content:center">${lucideIconToSvg("server", "rgb(56,189,248)", 16)}</div>
    <div style="background:rgba(15,23,42,0.85);color:#e2e8f0;font-size:10px;font-family:monospace;font-weight:600;padding:1px 5px;border-radius:3px;line-height:1.4;margin-top:3px;white-space:nowrap;text-align:center">
      <div>${server.label}</div>
      <div style="color:#94a3b8;font-weight:400">${server.name}</div>
    </div>
    <div style="width:0;height:0;border-left:4px solid transparent;border-right:4px solid transparent;border-top:5px solid rgba(15,23,42,0.9)"></div>`;
  return el;
}

// ── Photo spot marker helpers ─────────────────────────────────────────────────
const CAMERA_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z"/><circle cx="12" cy="13" r="3"/></svg>`;

function createPhotoSpotEl(spot: PhotoSpotListItem): HTMLElement {
  const el = document.createElement("div");
  el.style.cssText =
    "display:flex;flex-direction:column;align-items:center;filter:drop-shadow(0 1px 4px rgba(0,0,0,0.7));cursor:pointer";
  el.innerHTML = `
    <div style="background:rgba(168,85,247,0.9);border:2px solid rgba(216,180,254,0.8);border-radius:8px;padding:5px;display:flex;align-items:center;justify-content:center">${CAMERA_SVG}</div>
    <div style="background:rgba(15,23,42,0.85);color:#e2e8f0;font-size:10px;font-family:monospace;font-weight:600;padding:1px 5px;border-radius:3px;line-height:1.4;margin-top:3px;white-space:nowrap;max-width:120px;overflow:hidden;text-overflow:ellipsis">${spot.name}</div>
    <div style="width:0;height:0;border-left:4px solid transparent;border-right:4px solid transparent;border-top:5px solid rgba(168,85,247,0.9)"></div>`;
  return el;
}

function createPendingSpotEl(): HTMLElement {
  const el = document.createElement("div");
  el.style.cssText =
    "display:flex;flex-direction:column;align-items:center;filter:drop-shadow(0 1px 4px rgba(0,0,0,0.7))";
  el.innerHTML = `
    <div style="background:rgba(168,85,247,0.6);border:2px dashed rgba(216,180,254,0.9);border-radius:8px;padding:5px;display:flex;align-items:center;justify-content:center">${CAMERA_SVG}</div>
    <div style="width:0;height:0;border-left:4px solid transparent;border-right:4px solid transparent;border-top:5px solid rgba(168,85,247,0.6)"></div>`;
  return el;
}

// ── Component ─────────────────────────────────────────────────────────────────
export function LiveMapPage() {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const isMobile = useIsMobile();
  // On a phone the control stack would blanket the map, so it starts collapsed
  // behind a toggle. Desktop starts expanded but can still be collapsed.
  const [controlsOpen, setControlsOpen] = useState(true);
  const controlsInitedRef = useRef(false);
  useEffect(() => {
    if (controlsInitedRef.current) return;
    controlsInitedRef.current = true;
    if (isMobile) setControlsOpen(false);
  }, [isMobile]);
  const [cursor, setCursor] = useState<{ lat: number; lng: number } | null>(
    null,
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const searchMarkersRef = useRef<Map<string, maplibregl.Marker>>(new Map());

  const usersRef = useRef<Map<string, UserEntry>>(new Map());
  const markersRef = useRef<Map<string, MarkerEntry>>(new Map());
  // Off by default — connecting streams every nearby user's live location.
  const [showUsers, setShowUsers] = useState(false);
  const showUsersRef = useRef(false);

  // The same records as the stream, fetched once for the viewport instead of
  // pushed. No radius cap here, so this sees users the websocket won't send.
  const [showCurrentUsers, setShowCurrentUsers] = useState(false);
  const showCurrentUsersRef = useRef(false);
  const [currentUsersArea, setCurrentUsersArea] = useState<
    { lat: number; lng: number; radius_m: number } | null
  >(null);
  const currentUsersRef = useRef<LocationUpdate[]>([]);
  const currentUserMarkersRef = useRef<Map<string, MarkerEntry>>(new Map());

  const [showMapObjs, setShowMapObjs] = useState(false);
  const mapObjMarkersRef = useRef<Map<string, maplibregl.Marker>>(new Map());

  const [showServers, setShowServers] = useState(false);
  const showServersRef = useRef(false);
  const serversRef = useRef<ServerNode[]>([]);

  const [showH3, setShowH3] = useState(false);
  const [h3Res, setH3Res] = useState(3);
  const showH3Ref = useRef(false);
  const h3ResRef = useRef(3);

  const [showHeatmap, setShowHeatmap] = useState(true);
  const [heatmapDays, setHeatmapDays] = useState<7 | 30 | 90>(30);
  const [heatmapRes, setHeatmapRes] = useState<3 | 4 | 5 | 6 | 7>(5);
  const showHeatmapRef = useRef(false);
  const heatmapResRef = useRef<3 | 4 | 5 | 6 | 7>(5);
  const heatmapDataRef = useRef<LoginHeatmapCell[] | null>(null);
  const heatmapBaseDataRef = useRef<LoginHeatmapCell[] | null>(null);
  // One resolution coarser than the active one, used to fill gaps where the
  // active resolution has no cell (below min_count) with the "larger area"
  // value instead of leaving a hole. res3 is already the coarsest available.
  const heatmapBaseRes = heatmapRes > 3 ? ((heatmapRes - 1) as 3 | 4 | 5 | 6) : null;

  const queryClient = useQueryClient();
  const [showPhotoSpots, setShowPhotoSpots] = useState(false);
  const [photoSpotCenter, setPhotoSpotCenter] = useState<{ lat: number; lng: number; radius_m: number } | null>(null);
  const photoSpotMarkersRef = useRef<Map<string, maplibregl.Marker>>(new Map());
  const [createSpotMode, setCreateSpotMode] = useState(false);
  const [pendingSpotLoc, setPendingSpotLoc] = useState<{ lat: number; lng: number } | null>(null);
  const [createSpotName, setCreateSpotName] = useState("");
  const [createSpotDesc, setCreateSpotDesc] = useState("");
  const createSpotModeRef = useRef(false);
  const pendingSpotMarkerRef = useRef<maplibregl.Marker | null>(null);

  const { data: mapObjsData, isFetching: mapObjsFetching } = useQuery({
    queryKey: queryKeys.mapObjs(),
    queryFn: () => locationsProvider.getMapObjs(),
    enabled: showMapObjs,
    staleTime: 30_000,
  });

  const {
    data: currentUsersData,
    isFetching: currentUsersFetching,
    isError: currentUsersError,
  } = useQuery({
    queryKey: queryKeys.liveLocations(
      currentUsersArea?.lat ?? 0,
      currentUsersArea?.lng ?? 0,
      currentUsersArea?.radius_m ?? 0,
    ),
    queryFn: () =>
      usersProvider.allLiveLocations({
        lat: currentUsersArea!.lat,
        lng: currentUsersArea!.lng,
        radius_meters: currentUsersArea!.radius_m,
      }),
    enabled: showCurrentUsers && currentUsersArea !== null,
    // Positions expire 30s after they're reported, so anything older than that
    // is no longer a snapshot of who is out there.
    staleTime: 15_000,
    refetchOnWindowFocus: false,
  });

  const {
    data: serversData,
    isFetching: serversFetching,
    isError: serversError,
  } = useQuery({
    queryKey: queryKeys.shardingServers(),
    queryFn: () => shardingProvider.listServers(),
    enabled: showServers,
    staleTime: 5 * 60_000,
  });

  const { data: heatmapData, isFetching: heatmapFetching } = useQuery({
    queryKey: queryKeys.loginHeatmap(heatmapRes, heatmapDays, 1),
    queryFn: () => {
      const since = new Date();
      since.setDate(since.getDate() - heatmapDays);
      return adminProvider.getLoginHeatmap({ resolution: heatmapRes, since, minCount: 1 });
    },
    enabled: showHeatmap,
    // Cached per-resolution so re-entering a zoom band already visited this
    // session reuses the fetch instead of hitting the API again.
    staleTime: 5 * 60_000,
    placeholderData: keepPreviousData,
  });

  const { data: heatmapBaseData } = useQuery({
    queryKey: queryKeys.loginHeatmap(heatmapBaseRes ?? 0, heatmapDays, 1),
    queryFn: () => {
      const since = new Date();
      since.setDate(since.getDate() - heatmapDays);
      return adminProvider.getLoginHeatmap({ resolution: heatmapBaseRes!, since, minCount: 1 });
    },
    enabled: showHeatmap && heatmapBaseRes !== null,
    staleTime: 5 * 60_000,
    placeholderData: keepPreviousData,
  });

  const { data: photoSpotsData, isFetching: photoSpotsFetching } = useQuery({
    queryKey: queryKeys.photoSpots(photoSpotCenter?.lat ?? 0, photoSpotCenter?.lng ?? 0, photoSpotCenter?.radius_m),
    queryFn: () => photoSpotsProvider.listNear(photoSpotCenter!.lat, photoSpotCenter!.lng, photoSpotCenter!.radius_m),
    enabled: showPhotoSpots && photoSpotCenter !== null,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

  const createSpotMutation = useMutation({
    mutationFn: (input: { name: string; description?: string; lat: number; lng: number }) =>
      photoSpotsProvider.create(input),
    onSuccess: () => {
      toast.success("Photo spot created");
      setCreateSpotMode(false);
      createSpotModeRef.current = false;
      setPendingSpotLoc(null);
      setCreateSpotName("");
      setCreateSpotDesc("");
      if (showPhotoSpots) {
        queryClient.invalidateQueries({ queryKey: ["photo-spots"] });
      }
    },
    onError: (err: Error) => toast.error(err.message),
  });

  // Route state
  const [routeMode, setRouteMode] = useState(false);
  const [routeOrigin, setRouteOrigin] = useState<{ lat: number; lng: number } | null>(null);
  const [routeDest, setRouteDest] = useState<{ lat: number; lng: number } | null>(null);
  const [routeTraffic, setRouteTraffic] = useState(false);
  const [routeLoading, setRouteLoading] = useState(false);
  // Refs so map event handlers always see fresh values without re-registering
  const routeModeRef = useRef(false);
  const routePickStepRef = useRef<0 | 1>(0);
  const routeOriginRef = useRef<{ lat: number; lng: number } | null>(null);
  const routeTrafficRef = useRef(false);
  const serverMarkersRef = useRef<Map<string, maplibregl.Marker>>(new Map());
  const routeOriginMarkerRef = useRef<maplibregl.Marker | null>(null);
  const routeDestMarkerRef = useRef<maplibregl.Marker | null>(null);
  const routeGeoJsonRef = useRef<object | null>(null);

  function removeMarker(key: string) {
    const m = markersRef.current.get(key);
    if (!m) return;
    m.dot.remove();
    m.label.remove();
    markersRef.current.delete(key);
  }

  function upsertMarker(u: NeighborUser) {
    const map = mapRef.current;
    if (!map) return;
    const key = String(u.user_id);
    const clubed = !!u.current_club_name;

    if (markersRef.current.has(key)) {
      const m = markersRef.current.get(key)!;
      animateMarker(m.dot, { lat: u.lat, lng: u.lng }, SMOOTH_MS);
      animateMarker(m.label, { lat: u.lat, lng: u.lng }, SMOOTH_MS);
      const svg = m.dot.getElement().querySelector("svg");
      if (svg) {
        svg.setAttribute(
          "style",
          `transform:rotate(${u.heading}deg);transition:none`,
        );
        svg.setAttribute("fill", "#ffffff");
      }
      const inner = m.label.getElement().querySelector("div");
      if (inner) {
        inner.innerHTML = `
          <div style="background:rgba(15,23,42,0.85);color:#e2e8f0;font-size:11px;font-family:monospace;font-weight:600;padding:1px 5px;border-radius:3px;line-height:1.4">${u.username ?? key}</div>
          ${u.current_club_name ? `<div style="background:rgba(21,128,61,0.85);color:#bbf7d0;font-size:10px;font-family:monospace;padding:1px 5px;border-radius:3px;line-height:1.4">${u.current_club_name}</div>` : ""}`;
      }
    } else {
      const dot = new maplibregl.Marker({
        element: createArrowEl(clubed, u.heading),
      })
        .setLngLat([u.lng, u.lat])
        .addTo(map) as AnimatedMarker;
      const label = new maplibregl.Marker({
        element: createLabelEl(u.username ?? key, u.current_club_name),
        anchor: "top",
      })
        .setLngLat([u.lng, u.lat])
        .addTo(map) as AnimatedMarker;
      markersRef.current.set(key, { dot, label });
    }
  }

  function handleNeighborUpdate(payload: NeighborUser[]) {
    const ttl = Date.now() + USER_TTL_MS;
    payload.forEach((u) => {
      usersRef.current.set(String(u.user_id), { ...u, _ttl: ttl });
      upsertMarker(u);
    });
    updateUserServerLinks();
  }

  function clearCurrentUserMarkers() {
    currentUserMarkersRef.current.forEach((m) => {
      m.dot.remove();
      m.label.remove();
    });
    currentUserMarkersRef.current.clear();
  }

  function renderCurrentUsers(users: LocationUpdate[]) {
    const map = mapRef.current;
    if (!map) return;

    const live = new Set(users.map((u) => u.user_id));
    currentUserMarkersRef.current.forEach((m, id) => {
      if (live.has(id)) return;
      m.dot.remove();
      m.label.remove();
      currentUserMarkersRef.current.delete(id);
    });

    users.forEach((u) => {
      const existing = currentUserMarkersRef.current.get(u.user_id);
      if (existing) {
        animateMarker(existing.dot, { lat: u.lat, lng: u.lng }, SMOOTH_MS);
        animateMarker(existing.label, { lat: u.lat, lng: u.lng }, SMOOTH_MS);
        const svg = existing.dot.getElement().querySelector("svg");
        svg?.setAttribute(
          "style",
          `transform:rotate(${u.heading}deg);transition:none`,
        );
        return;
      }
      const dot = new maplibregl.Marker({
        element: createArrowEl(false, u.heading, CURRENT_USER_COLOR),
      })
        .setLngLat([u.lng, u.lat])
        .addTo(map) as AnimatedMarker;
      const label = new maplibregl.Marker({
        element: createCurrentUserLabelEl(
          u.username || u.user_id,
          u.current_club_name,
          u.connected_server_id,
        ),
        anchor: "top",
      })
        .setLngLat([u.lng, u.lat])
        .addTo(map) as AnimatedMarker;
      currentUserMarkersRef.current.set(u.user_id, { dot, label });
    });
  }

  // One white line per user whose websocket node is on the map. Only drawn
  // when both layers are on — there's nothing to connect otherwise.
  function updateUserServerLinks() {
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) return;

    const nodes = new Map(
      (showServersRef.current ? serversRef.current : []).map((s) => [s.id, s]),
    );

    const features: object[] = [];
    const link = (u: {
      lat: number;
      lng: number;
      connected_server_id?: string | null;
    }) => {
      const node = u.connected_server_id
        ? nodes.get(u.connected_server_id)
        : undefined;
      if (!node) return;
      features.push({
        type: "Feature",
        properties: {},
        geometry: {
          type: "LineString",
          coordinates: [
            [u.lng, u.lat],
            [node.lng, node.lat],
          ],
        },
      });
    };

    if (showUsersRef.current) usersRef.current.forEach(link);
    if (showCurrentUsersRef.current) currentUsersRef.current.forEach(link);

    const geojson = { type: "FeatureCollection", features };
    const source = map.getSource(LINK_SOURCE_ID) as
      | maplibregl.GeoJSONSource
      | undefined;
    if (source) {
      source.setData(geojson as any);
      return;
    }
    map.addSource(LINK_SOURCE_ID, { type: "geojson", data: geojson as any });
    map.addLayer({
      id: LINK_LAYER_ID,
      type: "line",
      source: LINK_SOURCE_ID,
      paint: {
        "line-color": "#ffffff",
        "line-width": 1,
        "line-opacity": 0.5,
      },
    });
  }

  useEffect(() => {
    showUsersRef.current = showUsers;
    showCurrentUsersRef.current = showCurrentUsers;
    showServersRef.current = showServers;
    serversRef.current = serversData ?? [];
    updateUserServerLinks();
  }, [showUsers, showCurrentUsers, showServers, serversData]);

  useEffect(() => {
    if (!showCurrentUsers) {
      currentUsersRef.current = [];
      clearCurrentUserMarkers();
      updateUserServerLinks();
      return;
    }
    currentUsersRef.current = currentUsersData ?? [];
    renderCurrentUsers(currentUsersRef.current);
    updateUserServerLinks();
  }, [showCurrentUsers, currentUsersData]);

  function currentViewportArea() {
    const map = mapRef.current;
    if (!map) return null;
    const center = map.getCenter();
    const ne = map.getBounds().getNorthEast();
    return {
      lat: center.lat,
      lng: center.lng,
      radius_m: haversineMeters(
        { lat: center.lat, lng: center.lng },
        { lat: ne.lat, lng: ne.lng },
      ),
    };
  }

  const { status: wsStatus, sendViewportUpdate } = useMapWs({
    onNeighborUpdate: handleNeighborUpdate,
    enabled: showUsers,
  });

  // Clear any live markers/state left over from before the toggle was
  // switched off, rather than leaving stale users frozen on the map.
  useEffect(() => {
    if (showUsers) return;
    usersRef.current.clear();
    markersRef.current.forEach((_, key) => removeMarker(key));
  }, [showUsers]);

  function sendCurrentViewport() {
    const map = mapRef.current;
    if (!map) return;
    const center = map.getCenter();
    const ne = map.getBounds().getNorthEast();
    const radius = haversineMeters(
      { lat: center.lat, lng: center.lng },
      { lat: ne.lat, lng: ne.lng },
    );
    sendViewportUpdate(center.lat, center.lng, radius);
  }

  function updateH3Grid() {
    const map = mapRef.current;
    if (!map) return;

    if (!showH3Ref.current) {
      if (map.getLayer("h3-grid-line")) map.removeLayer("h3-grid-line");
      if (map.getSource("h3-grid")) map.removeSource("h3-grid");
      return;
    }

    const cells = h3CellsForBounds(map.getBounds(), h3ResRef.current);
    if (cells.length > H3_MAX_CELLS) {
      toast.error("Too many H3 cells to render — zoom in or lower the resolution", {
        id: "h3-too-many",
      });
      return;
    }

    const geojson = h3CellsToGeoJson(cells);
    const source = map.getSource("h3-grid") as maplibregl.GeoJSONSource | undefined;
    if (source) {
      source.setData(geojson as any);
    } else {
      map.addSource("h3-grid", { type: "geojson", data: geojson as any });
    }
    if (!map.getLayer("h3-grid-line")) {
      map.addLayer({
        id: "h3-grid-line",
        type: "line",
        source: "h3-grid",
        layout: { "line-join": "round" },
        paint: { "line-color": "#f97316", "line-width": 1, "line-opacity": 0.7 },
      });
    }
  }

  const HEATMAP_FILL_COLOR = [
    "step",
    ["get", "count"],
    "#F6D8A8",
    10,
    "#EFA465",
    25,
    "#D65A3C",
    100,
    "#8E2335",
  ] as const;

  function removeHeatmapLayer(map: maplibregl.Map, id: string) {
    if (map.getLayer(`${id}-fill`)) map.removeLayer(`${id}-fill`);
    if (map.getLayer(`${id}-outline`)) map.removeLayer(`${id}-outline`);
    if (map.getSource(id)) map.removeSource(id);
  }

  function addOrUpdateHeatmapLayer(
    map: maplibregl.Map,
    id: string,
    cells: LoginHeatmapCell[],
    fillOpacity: number,
    beforeLayerId?: string,
  ) {
    const geojson = loginHeatmapToGeoJson(cells);
    const source = map.getSource(id) as maplibregl.GeoJSONSource | undefined;
    if (source) {
      source.setData(geojson as any);
    } else {
      map.addSource(id, { type: "geojson", data: geojson as any });
    }
    const before = beforeLayerId && map.getLayer(beforeLayerId) ? beforeLayerId : undefined;
    if (!map.getLayer(`${id}-fill`)) {
      map.addLayer(
        {
          id: `${id}-fill`,
          type: "fill",
          source: id,
          // Stepped scale — a few dense cities shouldn't wash out everything else.
          paint: { "fill-color": HEATMAP_FILL_COLOR as any, "fill-opacity": fillOpacity },
        },
        before,
      );
    }
    if (!map.getLayer(`${id}-outline`)) {
      map.addLayer(
        {
          id: `${id}-outline`,
          type: "line",
          source: id,
          paint: { "line-color": "rgba(255,255,255,0.6)", "line-width": 1 },
        },
        before,
      );
    }
  }

  function updateLoginHeatmap() {
    const map = mapRef.current;
    if (!map) return;

    if (!map.isStyleLoaded()) {
      map.once("idle", updateLoginHeatmap);
      return;
    }

    if (!showHeatmapRef.current) {
      removeHeatmapLayer(map, "login-heatmap-base");
      removeHeatmapLayer(map, "login-heatmap");
      return;
    }

    // Base layer (one resolution coarser) sits underneath, so wherever the
    // active resolution has no cell for an area, the larger area's existing
    // value shows through instead of a gap.
    if (heatmapBaseDataRef.current && heatmapBaseDataRef.current.length > 0) {
      addOrUpdateHeatmapLayer(map, "login-heatmap-base", heatmapBaseDataRef.current, 0.45, "login-heatmap-fill");
    } else {
      removeHeatmapLayer(map, "login-heatmap-base");
    }

    if (heatmapDataRef.current && heatmapDataRef.current.length > 0) {
      addOrUpdateHeatmapLayer(map, "login-heatmap", heatmapDataRef.current, 0.75);
    } else {
      removeHeatmapLayer(map, "login-heatmap");
    }
  }

  function updateHeatmapResFromZoom() {
    const map = mapRef.current;
    if (!map || !showHeatmapRef.current) return;
    const res = heatmapResForZoom(map.getZoom());
    if (res !== heatmapResRef.current) setHeatmapRes(res);
  }

  // Keep refs in sync with state for map event handlers
  useEffect(() => { routeModeRef.current = routeMode; }, [routeMode]);
  useEffect(() => { routeTrafficRef.current = routeTraffic; }, [routeTraffic]);
  useEffect(() => { createSpotModeRef.current = createSpotMode; }, [createSpotMode]);
  useEffect(() => { showH3Ref.current = showH3; }, [showH3]);
  useEffect(() => { h3ResRef.current = h3Res; }, [h3Res]);
  useEffect(() => { updateH3Grid(); }, [showH3, h3Res]);
  useEffect(() => { showHeatmapRef.current = showHeatmap; }, [showHeatmap]);
  useEffect(() => { heatmapResRef.current = heatmapRes; }, [heatmapRes]);
  useEffect(() => { heatmapDataRef.current = heatmapData ?? null; updateLoginHeatmap(); }, [heatmapData]);
  useEffect(() => { heatmapBaseDataRef.current = heatmapBaseRes === null ? null : (heatmapBaseData ?? null); updateLoginHeatmap(); }, [heatmapBaseData, heatmapBaseRes]);
  useEffect(() => { updateLoginHeatmap(); }, [showHeatmap]);
  useEffect(() => { if (showHeatmap) updateHeatmapResFromZoom(); }, [showHeatmap]);

  useEffect(() => {
    mapObjMarkersRef.current.forEach((m) => m.remove());
    mapObjMarkersRef.current.clear();

    const map = mapRef.current;
    if (!showMapObjs || !mapObjsData || !map) return;

    mapObjsData.forEach((obj, i) => {
      const marker = new maplibregl.Marker({ element: createMapObjEl(obj), anchor: "bottom" })
        .setLngLat([obj.lng, obj.lat])
        .addTo(map);
      mapObjMarkersRef.current.set(String(i), marker);
    });
  }, [showMapObjs, mapObjsData]);

  useEffect(() => {
    photoSpotMarkersRef.current.forEach((m) => m.remove());
    photoSpotMarkersRef.current.clear();

    const map = mapRef.current;
    if (!showPhotoSpots || !photoSpotsData || !map) return;

    photoSpotsData.data.forEach((spot) => {
      const marker = new maplibregl.Marker({ element: createPhotoSpotEl(spot), anchor: "bottom" })
        .setLngLat([spot.lng, spot.lat])
        .addTo(map);
      photoSpotMarkersRef.current.set(spot.spot_id, marker);
    });
  }, [showPhotoSpots, photoSpotsData]);

  useEffect(() => {
    pendingSpotMarkerRef.current?.remove();
    pendingSpotMarkerRef.current = null;

    const map = mapRef.current;
    if (!pendingSpotLoc || !map) return;

    pendingSpotMarkerRef.current = new maplibregl.Marker({ element: createPendingSpotEl(), anchor: "bottom" })
      .setLngLat([pendingSpotLoc.lng, pendingSpotLoc.lat])
      .addTo(map);

    return () => {
      pendingSpotMarkerRef.current?.remove();
      pendingSpotMarkerRef.current = null;
    };
  }, [pendingSpotLoc]);

  function addRouteToMap(map: maplibregl.Map, geometry: any) {
    if (!geometry) return;
    routeGeoJsonRef.current = geometry;
    
    // Determine if geometry is a full Feature or just a Geometry object
    let feature: any;
    if (geometry.type === "Feature") {
      feature = geometry;
    } else if (geometry.type === "FeatureCollection") {
      feature = geometry.features[0]; 
    } else if (typeof geometry === "object" && geometry.type) {
      feature = { type: "Feature", properties: {}, geometry };
    } else {
      console.warn("Invalid geometry received for route:", geometry);
      return;
    }

    if (map.getSource("route")) {
      (map.getSource("route") as maplibregl.GeoJSONSource).setData(feature);
    } else {
      map.addSource("route", { type: "geojson", data: feature });
    }

    // Always ensure layers are present
    if (!map.getLayer("route-line-casing")) {
      map.addLayer({
        id: "route-line-casing",
        type: "line",
        source: "route",
        layout: { "line-join": "round", "line-cap": "round" },
        paint: { "line-color": "#1e3a5f", "line-width": 7, "line-opacity": 0.6 },
      });
    }
    if (!map.getLayer("route-line")) {
      map.addLayer({
        id: "route-line",
        type: "line",
        source: "route",
        layout: { "line-join": "round", "line-cap": "round" },
        paint: { "line-color": "#3b82f6", "line-width": 4, "line-opacity": 0.95 },
      });
    }

    // Fit map to route
    try {
      const g = feature.geometry;
      if (g && (g.type === "LineString" || g.type === "MultiLineString")) {
        const coords = g.type === "LineString" ? g.coordinates : g.coordinates.flat();
        if (Array.isArray(coords) && coords.length > 0) {
          const bounds = new maplibregl.LngLatBounds(coords[0] as [number, number], coords[0] as [number, number]);
          for (const coord of coords) {
            bounds.extend(coord as [number, number]);
          }
          map.fitBounds(bounds, { padding: 100, maxZoom: 16 });
        }
      }
    } catch (e) {
      console.warn("Could not fit bounds to route", e);
    }
  }

  function clearRoute() {
    const map = mapRef.current;
    routeOriginMarkerRef.current?.remove();
    routeDestMarkerRef.current?.remove();
    routeOriginMarkerRef.current = null;
    routeDestMarkerRef.current = null;
    routeOriginRef.current = null;
    routeGeoJsonRef.current = null;
    routePickStepRef.current = 0;
    setRouteOrigin(null);
    setRouteDest(null);
    if (map) {
      if (map.getLayer("route-line")) map.removeLayer("route-line");
      if (map.getLayer("route-line-casing")) map.removeLayer("route-line-casing");
      if (map.getSource("route")) map.removeSource("route");
    }
  }

  async function fetchRoute(origin: { lat: number; lng: number }, dest: { lat: number; lng: number }) {
    const map = mapRef.current;
    if (!map) return;
    setRouteLoading(true);
    try {
      const traffic = routeTrafficRef.current;
      const data = await locationsProvider.getRoute(traffic, origin, dest);
      const encodedPolyline = data.routes?.[0]?.polyline?.encodedPolyline;
      if (!encodedPolyline) return;
      const geometry = { type: "LineString", coordinates: decodePolyline(encodedPolyline) };
      addRouteToMap(map, geometry);
    } catch (err) {
      console.error("Route fetch failed:", err);
    } finally {
      setRouteLoading(false);
    }
  }

  // Send viewport when WS connects
  useEffect(() => {
    if (wsStatus === "connected") sendCurrentViewport();
  }, [wsStatus]);

  useEffect(() => {
    const protocol = new Protocol();
    // Registered explicitly so the archive is read through the persistent range
    // cache; the style's pmtiles:// url resolves to this instance by key.
    protocol.add(cachedPMTiles(PMTILES_URL));
    maplibregl.addProtocol("pmtiles", protocol.tile);

    const map = new maplibregl.Map({
      container: containerRef.current!,
      center: [0, 20],
      zoom: 2,
    });
    // The hosted style hardcodes its own archive url; repoint it at PMTILES_URL so
    // the env decides which archive is read and tiles go through the cache above.
    map.setStyle(MAP_STYLE_URL, {
      transformStyle: (_, next) => withPMTilesSource(next, PMTILES_URL),
    });

    map.addControl(new maplibregl.NavigationControl(), "top-right");
    map.addControl(new maplibregl.ScaleControl(), "bottom-left");
    map.on("mousemove", (e) => {
      setCursor({ lat: e.lngLat.lat, lng: e.lngLat.lng });
      map.getCanvas().style.cursor = routeModeRef.current || createSpotModeRef.current ? "crosshair" : "";
    });
    map.on("mouseleave", () => setCursor(null));
    map.on("moveend", sendCurrentViewport);
    map.on("moveend", updateH3Grid);
    map.on("moveend", updateHeatmapResFromZoom);
    map.on("contextmenu", (e) => {
      e.preventDefault();
      const { lat, lng } = e.lngLat;
      const res = h3ResRef.current;
      const text = JSON.stringify(
        {
          lat,
          lng,
          h3_zoom_lvl: res,
          h3_cell_id: latLngToCell(lat, lng, res),
        },
        null,
        4,
      );
      navigator.clipboard.writeText(text);
      toast.success("Location copied to clipboard", { position: "top-center" });
    });

    map.on("click", (e) => {
      const { lat, lng } = e.lngLat;
      if (createSpotModeRef.current) {
        setPendingSpotLoc({ lat, lng });
        return;
      }
      if (!routeModeRef.current) return;
      if (routePickStepRef.current === 0) {
        routeOriginMarkerRef.current?.remove();
        routeOriginMarkerRef.current = new maplibregl.Marker({ element: createRoutePinEl("origin"), anchor: "bottom" })
          .setLngLat([lng, lat])
          .addTo(map);
        routeOriginRef.current = { lat, lng };
        setRouteOrigin({ lat, lng });
        routePickStepRef.current = 1;
      } else {
        routeDestMarkerRef.current?.remove();
        routeDestMarkerRef.current = new maplibregl.Marker({ element: createRoutePinEl("dest"), anchor: "bottom" })
          .setLngLat([lng, lat])
          .addTo(map);
        setRouteDest({ lat, lng });

        routePickStepRef.current = 0;
        const origin = routeOriginRef.current;
        if (origin) fetchRoute(origin, { lat, lng });
      }
    });

    // Re-add route line after style changes (setStyle clears all sources/layers)
    map.on("style.load", () => {
      if (routeGeoJsonRef.current) addRouteToMap(map, routeGeoJsonRef.current);
      updateH3Grid();
      updateLoginHeatmap();
      updateUserServerLinks();
    });

    mapRef.current = map;

    const evictInterval = setInterval(() => {
      const now = Date.now();
      let evicted = false;
      usersRef.current.forEach((u, key) => {
        if (u._ttl < now) {
          usersRef.current.delete(key);
          removeMarker(key);
          evicted = true;
        }
      });
      if (evicted) updateUserServerLinks();
    }, 1_000);

    return () => {
      clearInterval(evictInterval);
      markersRef.current.forEach((_, key) => removeMarker(key));
      clearCurrentUserMarkers();
      searchMarkersRef.current.forEach((m) => m.remove());
      searchMarkersRef.current.clear();
      mapObjMarkersRef.current.forEach((m) => m.remove());
      mapObjMarkersRef.current.clear();
      photoSpotMarkersRef.current.forEach((m) => m.remove());
      photoSpotMarkersRef.current.clear();
      pendingSpotMarkerRef.current?.remove();
      serverMarkersRef.current.forEach((m) => m.remove());
      serverMarkersRef.current.clear();
      routeOriginMarkerRef.current?.remove();
      routeDestMarkerRef.current?.remove();
      map.remove();
      mapRef.current = null;
      maplibregl.removeProtocol("pmtiles");
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const servers = showServers ? (serversData ?? []) : [];

    // Drop markers for nodes the latest response no longer lists.
    const live = new Set(servers.map((s) => s.id));
    serverMarkersRef.current.forEach((marker, id) => {
      if (live.has(id)) return;
      marker.remove();
      serverMarkersRef.current.delete(id);
    });

    servers.forEach((server) => {
      if (serverMarkersRef.current.has(server.id)) return;
      const marker = new maplibregl.Marker({
        element: createServerEl(server),
        anchor: "bottom",
      })
        .setLngLat([server.lng, server.lat])
        .addTo(map);
      serverMarkersRef.current.set(server.id, marker);
    });
  }, [showServers, serversData]);

  async function searchPlaces() {
    const q = searchQuery.trim();
    if (!q) return;
    const map = mapRef.current;
    if (!map) return;

    const center = map.getCenter();

    setSearchLoading(true);
    try {
      const data = await locationsProvider.search(q, center.lat, center.lng);

      searchMarkersRef.current.forEach((m) => m.remove());
      searchMarkersRef.current.clear();

      (data.places ?? []).forEach((place, i) => {
        const key = `${place.lat},${place.lng},${i}`;
        const marker = new maplibregl.Marker({
          element: createSearchPinEl(place.name, () => {
            marker.remove();
            searchMarkersRef.current.delete(key);
          }),
          anchor: "bottom",
        })
          .setLngLat([place.lng, place.lat])
          .addTo(map);
        searchMarkersRef.current.set(key, marker);
      });
    } catch (err) {
      console.error(err);
    } finally {
      setSearchLoading(false);
    }
  }

  return (
    <>
      <SiteHeader
        title="Live Map"
        status={
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <span
              className={`size-2 rounded-full ${
                !showUsers
                  ? "bg-muted-foreground/40"
                  : wsStatus === "connected"
                    ? "bg-green-500"
                    : wsStatus === "connecting"
                      ? "bg-yellow-500 animate-pulse"
                      : "bg-red-500"
              }`}
            />
            <span>
              {!showUsers
                ? "Users hidden"
                : wsStatus === "connected"
                  ? "Live"
                  : wsStatus === "connecting"
                    ? "Connecting…"
                    : "Disconnected"}
            </span>
          </div>
        }
      />
      <div className="flex flex-1 flex-col overflow-hidden relative">
        <div ref={containerRef} style={{ flex: 1 }} />
        {cursor && (
          <div className="absolute bottom-8 right-3 z-10 flex gap-3 rounded-md bg-background/90 backdrop-blur-sm shadow-md px-3 py-1.5 text-xs font-mono text-muted-foreground">
            <span>Lat: {cursor.lat.toFixed(5)}</span>
            <span>Lng: {cursor.lng.toFixed(5)}</span>
          </div>
        )}
        <Button
          type="button"
          size="icon"
          variant="outline"
          aria-label={controlsOpen ? "Hide map controls" : "Show map controls"}
          className="absolute top-3 left-3 z-20 size-9 bg-background/90 shadow-md backdrop-blur-sm"
          onClick={() => setControlsOpen((v) => !v)}
        >
          {controlsOpen ? (
            <IconX className="size-4" />
          ) : (
            <IconAdjustmentsHorizontal className="size-4" />
          )}
        </Button>
        <div
          className={cn(
            "absolute top-3 left-3 z-10 flex flex-col gap-2",
            isMobile &&
              "top-14 max-h-[calc(100%-4.5rem)] w-[calc(100%-1.5rem)] overflow-y-auto pb-2 pr-1",
            !isMobile && controlsOpen && "top-14",
            !controlsOpen && "hidden",
          )}
        >
          <form
            className="flex gap-1.5"
            onSubmit={(e) => {
              e.preventDefault();
              searchPlaces();
            }}
          >
            <Input
              placeholder="Search places…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-background/90 sm:w-48 backdrop-blur-sm shadow-md h-9 text-xs"
            />
            <Button
              type="submit"
              size="sm"
              disabled={searchLoading || !searchQuery.trim()}
              className="shadow-md bg-background/90 backdrop-blur-sm text-foreground hover:bg-background/95 h-9"
              variant="outline"
            >
              {searchLoading ? "…" : "Go"}
            </Button>
          </form>

          {/* Live users toggle — connects the websocket only when on */}
          <div className="flex items-center justify-between rounded-md bg-background/90 backdrop-blur-sm shadow-md px-3 py-2 w-full text-xs sm:w-48">
            <span className="font-semibold text-foreground">Live Users</span>
            <div className="flex items-center gap-1.5">
              {showUsers && wsStatus === "connecting" && (
                <span className="text-muted-foreground">…</span>
              )}
              <Switch checked={showUsers} onCheckedChange={setShowUsers} />
            </div>
          </div>

          {/* Current users — one fetch for the viewport instead of a stream */}
          <div className="flex flex-col gap-1.5 rounded-md bg-background/90 backdrop-blur-sm shadow-md px-3 py-2 w-full text-xs sm:w-48">
            <div className="flex items-center justify-between gap-2">
              <span className="min-w-0 truncate font-semibold text-foreground">
                Current Users
              </span>
              <Switch
                checked={showCurrentUsers}
                onCheckedChange={(next) => {
                  setShowCurrentUsers(next);
                  if (next) setCurrentUsersArea(currentViewportArea());
                }}
              />
            </div>
            {showCurrentUsers && (
              <>
                <div className="flex items-center justify-between gap-2">
                  <span className="min-w-0 truncate text-muted-foreground">
                    {currentUsersFetching
                      ? "Loading…"
                      : currentUsersError
                        ? "Failed to load"
                        : `${currentUsersData?.length ?? 0} user${currentUsersData?.length === 1 ? "" : "s"}`}
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-6 px-2 text-xs"
                    disabled={currentUsersFetching}
                    onClick={() => setCurrentUsersArea(currentViewportArea())}
                  >
                    Refresh
                  </Button>
                </div>
                <p className="text-muted-foreground">
                  Snapshot of this viewport. Positions expire after 30s.
                </p>
              </>
            )}
          </div>

          {/* Route panel */}
          <div className="flex flex-col gap-1.5 rounded-md bg-background/90 backdrop-blur-sm shadow-md px-3 py-2 w-full text-xs sm:w-48">
            <div className="flex items-center justify-between">
              <span className="font-semibold text-foreground">Route</span>
              <div className="flex gap-1">
                <Button
                  size="sm"
                  variant={routeMode ? "default" : "outline"}
                  className="h-6 px-2 text-xs"
                  onClick={() => {
                    const next = !routeMode;
                    setRouteMode(next);
                    if (!next) clearRoute();
                    else routePickStepRef.current = 0;
                  }}
                >
                  {routeMode ? "Cancel" : "Draw"}
                </Button>
                {(routeOrigin || routeDest) && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-6 px-2 text-xs"
                    onClick={clearRoute}
                  >
                    Clear
                  </Button>
                )}
              </div>
            </div>

            {routeMode && (
              <p className="text-muted-foreground">
                {!routeOrigin
                  ? "Click map to set origin (A)"
                  : routeLoading
                    ? "Fetching route…"
                    : "Click map to set destination (B)"}
              </p>
            )}

            {routeOrigin && (
              <div className="flex items-center gap-1 text-muted-foreground">
                <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-green-500 text-white font-bold text-[9px]">A</span>
                <span className="font-mono tabular-nums">
                  {routeOrigin.lat.toFixed(4)}, {routeOrigin.lng.toFixed(4)}
                </span>
              </div>
            )}
            {routeDest && (
              <div className="flex items-center gap-1 text-muted-foreground">
                <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-red-500 text-white font-bold text-[9px]">B</span>
                <span className="font-mono tabular-nums">
                  {routeDest.lat.toFixed(4)}, {routeDest.lng.toFixed(4)}
                </span>
              </div>
            )}

            {routeOrigin && routeDest && (
              <Button
                size="sm"
                className="h-6 px-2 text-xs w-full"
                disabled={routeLoading}
                onClick={() => fetchRoute(routeOrigin, routeDest)}
              >
                {routeLoading ? "Fetching…" : "Fetch Route"}
              </Button>
            )}

            <label className="flex items-center gap-1.5 cursor-pointer select-none text-muted-foreground">
              <input
                type="checkbox"
                checked={routeTraffic}
                onChange={(e) => setRouteTraffic(e.target.checked)}
                className="accent-blue-500"
              />
              Traffic-aware
            </label>
          </div>

          {/* Map objects toggle */}
          <div className="flex items-center justify-between rounded-md bg-background/90 backdrop-blur-sm shadow-md px-3 py-2 w-full text-xs sm:w-48">
            <span className="font-semibold text-foreground">Map Objects</span>
            <Button
              size="sm"
              variant={showMapObjs ? "default" : "outline"}
              className="h-6 px-2 text-xs"
              onClick={() => setShowMapObjs((v) => !v)}
              disabled={mapObjsFetching}
            >
              {mapObjsFetching ? "…" : showMapObjs ? "Hide" : "Show"}
            </Button>
          </div>

          {/* Servers */}
          <div className="flex flex-col gap-1 rounded-md bg-background/90 backdrop-blur-sm shadow-md px-3 py-2 w-full text-xs sm:w-48">
            <div className="flex items-center justify-between gap-2">
              <span className="min-w-0 truncate font-semibold text-foreground">
                Show Servers
              </span>
              <Switch checked={showServers} onCheckedChange={setShowServers} />
            </div>
            {showServers && (
              <span className="text-muted-foreground">
                {serversFetching
                  ? "Loading…"
                  : serversError
                    ? "Failed to load nodes"
                    : `${serversData?.length ?? 0} node${serversData?.length === 1 ? "" : "s"}`}
              </span>
            )}
          </div>

          {/* H3 grid */}
          <div className="flex flex-col gap-1.5 rounded-md bg-background/90 backdrop-blur-sm shadow-md px-3 py-2 w-full text-xs sm:w-48">
            <div className="flex items-center justify-between">
              <span className="font-semibold text-foreground">H3 Grid</span>
              <Switch checked={showH3} onCheckedChange={setShowH3} />
            </div>
            {showH3 && (
              <div className="flex flex-col gap-1">
                <div className="flex items-center justify-between text-muted-foreground">
                  <span>Resolution</span>
                  <span className="tabular-nums">{h3Res}</span>
                </div>
                <Slider
                  min={0}
                  max={15}
                  step={1}
                  value={[h3Res]}
                  onValueChange={([v]) => setH3Res(v)}
                />
              </div>
            )}
          </div>

          {/* Login heatmap */}
          <div className="flex flex-col gap-1.5 rounded-md bg-background/90 backdrop-blur-sm shadow-md px-3 py-2 w-full text-xs sm:w-48">
            <div className="flex items-center justify-between">
              <span className="font-semibold text-foreground">Login Heatmap</span>
              <div className="flex items-center gap-1.5">
                {heatmapFetching && <span className="text-muted-foreground">…</span>}
                <Switch checked={showHeatmap} onCheckedChange={setShowHeatmap} />
              </div>
            </div>
            {showHeatmap && (
              <>
                <div className="flex items-center justify-between text-muted-foreground">
                  <span>Last active</span>
                  <div className="flex gap-1">
                    {([7, 30, 90] as const).map((d) => (
                      <Button
                        key={d}
                        size="sm"
                        variant={heatmapDays === d ? "default" : "outline"}
                        className="h-5 px-1.5 text-[10px]"
                        onClick={() => setHeatmapDays(d)}
                      >
                        {d}d
                      </Button>
                    ))}
                  </div>
                </div>
                <div className="flex items-center justify-between text-muted-foreground">
                  <span>Resolution</span>
                  <span className="tabular-nums">{heatmapRes} (auto)</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="flex flex-1 items-center overflow-hidden rounded-sm">
                    <span className="flex-1 h-2.5" style={{ background: "#F6D8A8" }} />
                    <span className="flex-1 h-2.5" style={{ background: "#EFA465" }} />
                    <span className="flex-1 h-2.5" style={{ background: "#D65A3C" }} />
                    <span className="flex-1 h-2.5" style={{ background: "#8E2335" }} />
                  </div>
                </div>
                <div className="flex items-center justify-between text-muted-foreground">
                  <span>5–9</span>
                  <span>10–24</span>
                  <span>25–99</span>
                  <span>100+</span>
                </div>
                <p className="text-muted-foreground">
                  Where users were last active.
                </p>
              </>
            )}
          </div>

          {/* Photo spots */}
          <div className="flex flex-col gap-1.5 rounded-md bg-background/90 backdrop-blur-sm shadow-md px-3 py-2 w-full text-xs sm:w-48">
            <div className="flex items-center justify-between">
              <span className="font-semibold text-foreground">Photo Spots</span>
              <div className="flex gap-1">
                <Button
                  size="sm"
                  variant={showPhotoSpots ? "default" : "outline"}
                  className="h-6 px-2 text-xs"
                  disabled={photoSpotsFetching}
                  onClick={() => {
                    const next = !showPhotoSpots;
                    setShowPhotoSpots(next);
                    if (next) {
                      const map = mapRef.current;
                      if (map) {
                        const center = map.getCenter();
                        const ne = map.getBounds().getNorthEast();
                        const radius_m = haversineMeters(
                          { lat: center.lat, lng: center.lng },
                          { lat: ne.lat, lng: ne.lng },
                        );
                        setPhotoSpotCenter({ lat: center.lat, lng: center.lng, radius_m });
                      }
                    }
                  }}
                >
                  {photoSpotsFetching ? "…" : showPhotoSpots ? "Hide" : "Show"}
                </Button>
                <Button
                  size="sm"
                  variant={createSpotMode ? "default" : "outline"}
                  className="h-6 px-2 text-xs"
                  onClick={() => {
                    const next = !createSpotMode;
                    setCreateSpotMode(next);
                    if (!next) {
                      setPendingSpotLoc(null);
                      setCreateSpotName("");
                      setCreateSpotDesc("");
                    }
                  }}
                >
                  Add
                </Button>
              </div>
            </div>

            {createSpotMode && !pendingSpotLoc && (
              <p className="text-muted-foreground">Click map to place spot</p>
            )}

            {createSpotMode && pendingSpotLoc && (
              <div className="flex flex-col gap-1.5">
                <span className="font-mono tabular-nums text-muted-foreground">
                  {pendingSpotLoc.lat.toFixed(4)}, {pendingSpotLoc.lng.toFixed(4)}
                </span>
                <Input
                  placeholder="Name *"
                  value={createSpotName}
                  onChange={(e) => setCreateSpotName(e.target.value)}
                  className="h-7 text-xs"
                />
                <Input
                  placeholder="Description"
                  value={createSpotDesc}
                  onChange={(e) => setCreateSpotDesc(e.target.value)}
                  className="h-7 text-xs"
                />
                <div className="flex gap-1">
                  <Button
                    size="sm"
                    className="h-6 px-2 text-xs flex-1"
                    disabled={!createSpotName.trim() || createSpotMutation.isPending}
                    onClick={() => {
                      if (!createSpotName.trim() || !pendingSpotLoc) return;
                      createSpotMutation.mutate({
                        name: createSpotName.trim(),
                        description: createSpotDesc.trim() || undefined,
                        lat: pendingSpotLoc.lat,
                        lng: pendingSpotLoc.lng,
                      });
                    }}
                  >
                    {createSpotMutation.isPending ? "…" : "Create"}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-6 px-2 text-xs"
                    onClick={() => setPendingSpotLoc(null)}
                  >
                    Repin
                  </Button>
                </div>
              </div>
            )}

            {showPhotoSpots && photoSpotsData && !createSpotMode && (
              <p className="text-muted-foreground">{photoSpotsData.data.length} spot{photoSpotsData.data.length !== 1 ? "s" : ""} loaded</p>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
